import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

// 3PL Logistics — Sponsor Network: Temperature master-data tab
// FSD default values: Ambient, Coldstore, Dual (Ambient + Coldstore)
// Drives the Truck Temperature dropdown in the Schedule Shipment popup and
// the per-batch eligibility check during co-shipment batch selection.

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(2),
    gap: "32px",
  },
  tableSection: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "600px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": { padding: "6px 8px", height: "30px" },
  },
  addButton: {
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  addIconCircle: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    border: "1.5px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "14px",
    fontWeight: 600,
  },
  actionButtons: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingTop: theme.spacing(1),
  },
  saveButton: {
    backgroundColor: "#E8783D",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    minWidth: "160px",
    "&:hover": { backgroundColor: "#cf6830" },
    "&:disabled": { backgroundColor: "#f0b590", color: "#fff" },
  },
  resetButton: {
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    minWidth: "160px",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  switchBase: {
    "&.Mui-checked": { color: "#007bff" },
    "&.Mui-checked + .MuiSwitch-track": { backgroundColor: "#007bff" },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: { padding: "2px" },
  deleteIcon: { color: "#d32f2f" },
}));

// Default FSD values shown when backend hasn't been wired yet
const DEFAULT_TEMPERATURES = [
  { id: "TMP-1", name: "Ambient",                       selected: true,  isNew: false, type: "Temperature" },
  { id: "TMP-2", name: "Coldstore",                     selected: true,  isNew: false, type: "Temperature" },
  { id: "TMP-3", name: "Dual (Ambient + Coldstore)",    selected: true,  isNew: false, type: "Temperature" },
];

const TemperatureTab = (props) => {
  const { featuresAccess, sponsorId, setTemperature } = props;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingStates, setSavingStates] = useState({});
  const [isSavingSelection, setIsSavingSelection] = useState(false);

  const [temperatures, setTemperatures] = useState(DEFAULT_TEMPERATURES);
  const [originalData, setOriginalData] = useState(DEFAULT_TEMPERATURES);

  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? true;

  // Fetch from /fetchTemperatureDetails/{networkConfig} per spec.
  // networkConfig=true → return all rows (incl. deactivated / markForDelete);
  // used by Sponsor Network Config screen. Falls back to FSD defaults if the
  // endpoint is not yet live so the tab still renders during early integration.
  const fetchTemperatures = async () => {
    setError(null);
    setLoading(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchTemperatureDetails/true`;
      const response = await axios.get(url);
      const list = response?.data?.temperatureDetails || response?.data?.data?.temperatureDetails || [];
      if (list.length > 0) {
        const formatted = list.map((item) => ({
          id: item.id,
          name: item.temperature || "",
          selected: !!item.activated,
          markForDelete: !!item.markForDelete,
          isNew: false,
          lastUpdatedBy: item.lastUpdatedBy,
          lastUpdatedDate: item.lastUpdatedDate,
        })).filter((t) => !t.markForDelete);
        setTemperatures(formatted);
        setOriginalData(formatted);
      }
      // If list is empty, keep FSD defaults already in state.
    } catch (err) {
      // Endpoint may not exist yet — keep FSD static defaults silently
      console.warn("fetchTemperatureDetails: endpoint not ready — showing FSD defaults");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemperatures();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build payload exactly per spec for /updateTemperatureDetails
  const buildUpdatePayload = (changedItems) => {
    return {
      temperatureDetails: changedItems.map((t) => ({
        ...(t.id && !String(t.id).startsWith("TMP-LOCAL") ? { id: t.id } : {}),
        temperature: t.name,
        activated: !!t.selected,
        markForDelete: !!t.markForDelete,
      })),
    };
  };

  // Toggle only updates local state; Save Selection persists the changes
  const handleToggle = (index) => {
    const updated = temperatures.map((item, i) =>
      i === index ? { ...item, selected: !item.selected } : item
    );
    setTemperatures(updated);
  };

  // Saves current toggle selection for all existing (non-new) rows
  const handleSaveSelection = async () => {
    const existingRows = temperatures.filter((t) => !t.isNew);
    if (existingRows.length === 0 || isSavingSelection) return;
    setIsSavingSelection(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateTemperatureDetails`;
      await axios.post(url, buildUpdatePayload(existingRows));
      setOriginalData(temperatures.filter((t) => !t.isNew));
      setTemperature && setTemperature((prev) => !prev);
    } catch (err) {
      // Endpoint not ready — keep local state as-is
    }
    setIsSavingSelection(false);
  };

  // Resets toggle state back to what was last fetched from the server
  const handleResetSelection = () => {
    setTemperatures((prev) => {
      const newRows = prev.filter((t) => t.isNew);
      return [...originalData.map((o) => ({ ...o })), ...newRows];
    });
  };

  const handleNameChange = (index, value) => {
    const updated = [...temperatures];
    updated[index].name = value;
    setTemperatures(updated);
  };

  const handleAddTemperature = () => {
    setTemperatures([
      ...temperatures,
      // New rows default to selected: true so the toggle is ON when adding
      { name: "", selected: true, isNew: true, type: "Temperature" },
    ]);
  };

  const handleCancel = (index) => {
    setTemperatures((prev) => prev.filter((_, i) => i !== index));
  };

  // Delete = soft delete via markForDelete=true on /updateTemperatureDetails
  const handleDelete = async (index) => {
    const item = temperatures[index];
    if (item.isNew) {
      setTemperatures((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    // Show the table loader while the soft-delete + refresh runs — matches the
    // other Sponsor Network tabs (ShipmentStatus/BatchStatus etc.) which re-fetch
    // after delete so the user sees a spinner instead of an instant blank row.
    setLoading(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateTemperatureDetails`;
      await axios.post(url, buildUpdatePayload([{ ...item, markForDelete: true }]));
      await fetchTemperatures(); // refreshes from server + clears loader in its finally
      setTemperature && setTemperature((prev) => !prev);
    } catch (err) {
      // Endpoint not ready — still remove from UI for visual feedback
      setTemperatures((prev) => prev.filter((_, i) => i !== index));
      setOriginalData((prev) => prev.filter((o) => o.id !== item.id));
      setTemperature && setTemperature((prev) => !prev);
      setLoading(false);
    }
  };

  // Save new row = POST to /updateTemperatureDetails without an id (Corda auto-generates)
  const addTemperature = async (index) => {
    const item = temperatures[index];
    if (!item.name?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateTemperatureDetails`;
      const response = await axios.post(url, buildUpdatePayload([{ ...item, markForDelete: false }]));
      if (response.data?.status === true || response.data?.status === "true") {
        fetchTemperatures();
        setTemperature && setTemperature((prev) => !prev);
      } else {
        // Backend returned non-success — keep new row locally for now
        setTemperatures((prev) =>
          prev.map((it, i) =>
            i === index ? { ...it, isNew: false, id: `TMP-LOCAL-${Date.now()}` } : it
          )
        );
      }
    } catch (err) {
      // Endpoint not ready — keep new row locally so UI still works
      setTemperatures((prev) =>
        prev.map((it, i) =>
          i === index ? { ...it, isNew: false, id: `TMP-LOCAL-${Date.now()}` } : it
        )
      );
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  return (
    <div className={classes.container}>
      <div className={classes.tableSection}>
        <Table className={`${classes.table} default-table-styles`}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Selection</TableCell>
              <TableCell className={classes.headerCell}>Temperature</TableCell>
              <TableCell className={classes.headerCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80px" }}>
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", backgroundColor: "#ffecec", color: "#b00020", border: "1px solid #ff9999" }}>
                    <Typography>{error}</Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              temperatures.map((item, index) => (
                <TableRow key={item.id || `new-${index}`}>
                  <TableCell className={classes.bodyCell}>
                    <Switch
                      checked={!!item.selected}
                      onChange={() => handleToggle(index)}
                      classes={{ switchBase: classes.switchBase }}
                      disabled={!canEdit}
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Type Text Here"
                          value={item.name}
                          onChange={(e) => handleNameChange(index, e.target.value)}
                          onBlur={(e) => {
                            const v = e.target.value;
                            if (typeof v === "string" && v.trim() !== v) handleNameChange(index, v.trim());
                          }}
                          className={classes.textField}
                          variant="outlined"
                          size="small"
                          inputProps={{
                            onBeforeInput: (e) => {
                              if (!/^[a-zA-Z0-9\s\-()+]+$/.test(e.data)) e.preventDefault();
                            },
                          }}
                        />
                        <div className={classes.inlineActions}>
                          <IconButton onClick={() => handleCancel(index)} className={classes.iconBtn} title="Cancel">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => addTemperature(index)}
                            className={classes.iconBtn}
                            title="Save"
                            disabled={
                              !item.name?.trim() ||
                              temperatures.some(
                                (s, i) =>
                                  i !== index &&
                                  s.name?.trim().toLowerCase() === item.name?.trim().toLowerCase()
                              ) ||
                              savingStates[index]
                            }
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      item.name
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {canEdit && (
                      <IconButton
                        onClick={() => handleDelete(index)}
                        className={classes.iconBtn}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" className={classes.deleteIcon} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {canEdit && (
          <div>
          <Button className={classes.addButton} onClick={handleAddTemperature}>
            <span className={classes.addIconCircle}>+</span>
            Add New Temperature
          </Button>
          </div>
        )}
      </div>

      {/* Save Selection / Reset Selection placed to the right of the table,
          matching the Document Groups tab. */}
      {canEdit && (
        <div className={classes.actionButtons}>
          <Button
            className={classes.saveButton}
            onClick={handleSaveSelection}
            disabled={isSavingSelection}
          >
            {isSavingSelection ? "Saving..." : "Save Selection"}
          </Button>
          <Button className={classes.resetButton} onClick={handleResetSelection}>
            Reset Selection
          </Button>
        </div>
      )}
    </div>
  );
};

export default TemperatureTab;
